import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Wallet, Building2, CreditCard, Loader2,
    RotateCcw, Send, ChevronDown, Hash,
    CheckCircle, Navigation, ListChecks, Table2,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';
import { formatIndianCurrency, getAmountDisplay } from '../../utilities/amountToTextHelper';

import {
    fetchConsolidateNoForLBSep,
    fetchLBCostcenterPaidAmount,
    fetchCompanyBankDetails,
    saveLBSEPPayment,
    clearPaidData,
    clearSaveResult,
    resetLabourSepPayment,
    selectConsolidateNumbersArray,
    selectConsolidateNumbersLoading,
    selectPaidData,
    selectPaidDataLoading,
    selectBankListArray,
    selectBankListLoading,
    selectSaveStatus,
    selectSaveLoading,
    selectSaveError,
} from '../../slices/HRSlice/labourSepPaymentSlice';

import {
    fetchChequeNumbers,
    selectChequeNumbersArray,
    selectChequeNumbersLoading,
    resetChequeNumbersData,
} from '../../slices/bankSlice/chequeNumbersSlice';

// ── Constants ─────────────────────────────────────────────────────────────────

// 'LabourAdvance' = approved Bulk Labour Advance CMS batches; the backend pays it exactly like 'Wages'.
export const PAYMENT_FOR_OPTIONS = [
    { value: 'Wages', label: 'Wages' },
    { value: 'LabourAdvance', label: 'Advance' },
    { value: 'LabourPF', label: 'Labour PF' },
    { value: 'LabourESI', label: 'Labour ESI' },
];

const MODE_OPTIONS = ['Cheque', 'RTGS/E-Trans', 'DD'];

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDateForAPI = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && /^\d{2}-[A-Za-z]{3}-\d{4}$/.test(val)) return val;
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
        const [yyyy, mm, dd] = val.split('T')[0].split('-');
        return `${dd}-${MONTH_ABBR[parseInt(mm, 10) - 1]}-${yyyy}`;
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

// ── Shared UI ─────────────────────────────────────────────────────────────────

const inputCls =
    'w-full px-3.5 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-teal-500 focus:ring-teal-100 dark:focus:ring-teal-900/30 hover:border-gray-300 transition-all disabled:opacity-60 disabled:cursor-not-allowed';

const Label = ({ children, required }) => (
    <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1.5">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectIcon = ({ loading }) => (
    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
        {loading
            ? <Loader2 className="h-4 w-4 text-teal-500 animate-spin" />
            : <ChevronDown className="h-4 w-4 text-gray-400" />}
    </div>
);

const SectionHeader = ({ icon: Icon, title, subtitle, action }) => (
    <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/60 dark:bg-gray-900/40 rounded-t-2xl">
        <div className="flex items-center gap-3">
            <Icon className="h-4 w-4 text-teal-500" />
            <div>
                <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">{title}</h2>
                {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500">{subtitle}</p>}
            </div>
        </div>
        {action}
    </div>
);

const InnerHeader = ({ icon: Icon, title, subtitle }) => (
    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100 dark:border-gray-700">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-md">
            <Icon className="h-5 w-5 text-white" />
        </div>
        <div>
            <h3 className="text-base font-bold text-gray-800 dark:text-gray-100">{title}</h3>
            {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
    </div>
);

// ── Consolidate No multi-select dropdown ────────────────────────────────────

const ConsolidateNoDropdown = ({ options, selected, onChange, loading, disabled }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const allKeys = options.map((o) => o.No);
    const allSelected = allKeys.length > 0 && allKeys.every((k) => selected.includes(k));

    const toggleOne = (no) => {
        if (selected.includes(no)) onChange(selected.filter((v) => v !== no));
        else onChange([...selected, no]);
    };
    const toggleAll = () => {
        onChange(allSelected ? [] : allKeys);
    };

    const summary = selected.length === 0
        ? 'Select Consolidate No'
        : `${selected.length} selected`;

    return (
        <div className="relative" ref={containerRef}>
            <button
                type="button"
                onClick={() => !disabled && setIsOpen((o) => !o)}
                disabled={disabled}
                className={`${inputCls} flex items-center justify-between text-left`}
            >
                <span className={selected.length ? 'text-gray-800 dark:text-gray-100' : 'text-gray-400'}>
                    {loading ? 'Loading…' : summary}
                </span>
                <SelectIcon loading={loading} />
            </button>

            {isOpen && !disabled && (
                <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
                    {options.length === 0 ? (
                        <div className="px-4 py-3 text-xs text-gray-400">No Consolidate Nos Exist</div>
                    ) : (
                        <>
                            <label className="flex items-center gap-2 px-4 py-2 text-xs font-bold text-teal-600 dark:text-teal-300 border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-teal-50 dark:hover:bg-teal-900/20">
                                <input type="checkbox" checked={allSelected} onChange={toggleAll} className="accent-teal-600" />
                                Select All
                            </label>
                            {options.map((o) => (
                                <label
                                    key={o.No}
                                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selected.includes(o.No)}
                                        onChange={() => toggleOne(o.No)}
                                        className="accent-teal-600"
                                    />
                                    {o.Desc}
                                </label>
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

// ── Component ─────────────────────────────────────────────────────────────────

const LabourSepPayment = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);

    const roleId = userData?.roleId || userData?.RID || 0;
    const userName = userData?.userName || userData?.UserName || 'system';

    const currentYear = new Date().getFullYear();

    // ── Redux ──────────────────────────────────────────────────────────────
    const consolidateOptions = useSelector(selectConsolidateNumbersArray);
    const consolidateLoading = useSelector(selectConsolidateNumbersLoading);
    const paidData = useSelector(selectPaidData);
    const paidDataLoading = useSelector(selectPaidDataLoading);
    const bankList = useSelector(selectBankListArray);
    const bankLoading = useSelector(selectBankListLoading);
    const chequeList = useSelector(selectChequeNumbersArray);
    const chequeLoading = useSelector(selectChequeNumbersLoading);
    const saveStatus = useSelector(selectSaveStatus);
    const saveLoading = useSelector(selectSaveLoading);
    const saveError = useSelector(selectSaveError);

    // ── Step 1: config ─────────────────────────────────────────────────────
    const [paymentFor, setPaymentFor] = useState('');
    const [year, setYear] = useState(String(currentYear));
    const [month, setMonth] = useState('');

    // ── Step 2: consolidate no ────────────────────────────────────────────
    const [selectedConsNos, setSelectedConsNos] = useState([]);
    const [viewed, setViewed] = useState(false);

    // ── Step 3: bank & payment ────────────────────────────────────────────
    const [selectedBank, setSelectedBank] = useState(null);
    const [modeOfPay, setModeOfPay] = useState('');
    const [chequeId, setChequeId] = useState('');
    const [refNo, setRefNo] = useState('');
    const [payDate, setPayDate] = useState(null);
    const [remarks, setRemarks] = useState('');

    // ── Init ──────────────────────────────────────────────────────────────
    useEffect(() => {
        dispatch(fetchCompanyBankDetails());
        return () => {
            dispatch(resetLabourSepPayment());
            dispatch(resetChequeNumbersData());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // Reset downstream when Payment For changes
    useEffect(() => {
        setYear(String(currentYear));
        setMonth('');
        setSelectedConsNos([]);
        setViewed(false);
        dispatch(clearPaidData());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentFor]);

    // Reset downstream when Year changes
    useEffect(() => {
        setMonth('');
        setSelectedConsNos([]);
        setViewed(false);
        dispatch(clearPaidData());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [year]);

    // Fetch consolidate numbers once PaymentFor + Year + Month are set
    useEffect(() => {
        setSelectedConsNos([]);
        setViewed(false);
        dispatch(clearPaidData());
        if (paymentFor && year && month) {
            dispatch(fetchConsolidateNoForLBSep({ paymentFor, month, year }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [month]);

    // Reset cheque data when bank changes
    useEffect(() => {
        if (!selectedBank) { dispatch(resetChequeNumbersData()); return; }
        setModeOfPay('');
        setChequeId('');
        setRefNo('');
        dispatch(fetchChequeNumbers(selectedBank.Bank_Name));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBank]);

    useEffect(() => {
        setChequeId('');
        setRefNo('');
    }, [modeOfPay]);

    // Save result effect
    useEffect(() => {
        if (saveStatus === 'success') {
            const refno = (saveError || '').split('$')[1];
            toast.success(refno ? `Transaction Ref No. ${refno} Created Successfully` : 'Saved Successfully.');
            dispatch(clearSaveResult());
            handleFullReset();
        } else if (saveStatus === 'failed' && saveError) {
            toast.error(saveError);
            dispatch(clearSaveResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveStatus]);

    // ── Handlers ──────────────────────────────────────────────────────────
    const handleFullReset = () => {
        setPaymentFor(''); setYear(String(currentYear)); setMonth('');
        setSelectedConsNos([]); setViewed(false);
        setSelectedBank(null); setModeOfPay(''); setChequeId(''); setRefNo('');
        setPayDate(null); setRemarks('');
        dispatch(resetLabourSepPayment());
        dispatch(resetChequeNumbersData());
        dispatch(fetchCompanyBankDetails());
    };

    const handleView = () => {
        if (!paymentFor || !year || !month) { toast.warn('Select Payment For, Year and Month.'); return; }
        if (selectedConsNos.length === 0) { toast.warn('Select Consolidate No.'); return; }
        dispatch(fetchLBCostcenterPaidAmount({
            paymentFor, month, year, selectedConsolidateno: selectedConsNos.map(String),
        }));
        setViewed(true);
    };

    const isLabourPF = paymentFor === 'LabourPF';
    const paidRows = paidData?.PaidDataList || [];
    const gridTotal = useMemo(() => {
        return paidRows.reduce((sum, r) => sum + (r.CCAmount || 0) + (isLabourPF ? (r.EmployerAmount || 0) : 0), 0);
    }, [paidRows, isLabourPF]);

    const handleSubmit = () => {
        if (!selectedBank) { toast.warn('Select a bank account.'); return; }
        if (!modeOfPay) { toast.warn('Select mode of payment.'); return; }
        if (modeOfPay === 'Cheque' && !chequeId) { toast.warn('Select a cheque number.'); return; }
        if (modeOfPay !== 'Cheque' && !refNo.trim()) { toast.warn('Enter the payment number.'); return; }
        if (!payDate) { toast.warn('Select payment date.'); return; }
        if (!remarks.trim()) { toast.warn('Enter remarks.'); return; }
        if (gridTotal <= 0) { toast.warn('CC/Job Amount Does Not Exist.'); return; }

        let ccCodes = '', ccAmounts = '', contribs = '';
        if (isLabourPF) {
            paidRows.forEach((r) => {
                ccCodes += `${r.CCCode},`;
                contribs += 'Employee,';
                ccAmounts += `${r.CCAmount},`;

                ccCodes += `${r.CCCode},`;
                contribs += 'Employer,';
                ccAmounts += `${r.EmployerAmount},`;
            });
        } else {
            paidRows.forEach((r) => {
                ccCodes += `${r.CCCode},`;
                ccAmounts += `${r.CCAmount},`;
            });
        }

        const paymentNo = modeOfPay === 'Cheque'
            ? (chequeList.find((c) => c.Cheque_Id === chequeId)?.Cheque_No || chequeId)
            : refNo;

        dispatch(saveLBSEPPayment({
            paymentFor, month, year,
            ccCodes, ccAmounts, contributionTypes: contribs,
            bankId: selectedBank.Bank_Id,
            modeOfPay, paymentNo,
            bankTotal: gridTotal,
            paymentDate: formatDateForAPI(payDate),
            remarks,
            selectedConsolidateno: selectedConsNos.map(String),
            roleId, createdBy: userName,
        }));
    };

    // ── Derived ───────────────────────────────────────────────────────────
    const isBusy = saveLoading || paidDataLoading || bankLoading;
    const yearOptions = [String(currentYear), String(currentYear - 1)];
    const selectedCheqNo = chequeList.find((c) => c.Cheque_Id === chequeId)?.Cheque_No || '—';
    const refDisplay = modeOfPay === 'Cheque' ? selectedCheqNo : (refNo || '—');
    const dateDisplay = payDate
        ? new Date(payDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')
        : '—';

    const status = paidData?.PaymentExistStatus;
    const showGrid = viewed && status === 'Exist';
    const showNotExist = viewed && status === 'NotExist';
    const showNoData = viewed && status && status !== 'Exist' && status !== 'NotExist';

    const showSummary = showGrid && selectedBank && modeOfPay &&
        (modeOfPay === 'Cheque' ? !!chequeId : !!refNo) && payDate;
    const canSubmit = showSummary && !saveLoading && remarks.trim();

    // ── Render ────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/30 to-emerald-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6">

            {/* ── Page Header ──────────────────────────────────────────── */}
            <div className="max-w-7xl mx-auto mb-6">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-teal-600 via-emerald-600 to-green-700 shadow-xl shadow-teal-500/20 p-7 text-white">
                    <div className="absolute inset-0 opacity-10"
                        style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
                    <div className="absolute top-0 right-0 w-72 h-72 bg-emerald-500 rounded-full -translate-y-1/2 translate-x-1/4 opacity-20 blur-3xl" />
                    <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-lg border border-white/20">
                                <Wallet className="h-7 w-7 text-white" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-bold text-teal-200 uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">HR Module</span>
                                </div>
                                <h1 className="text-2xl md:text-3xl font-black tracking-tight">
                                    {menuData?.name || 'Labour Salary/PF/ESI Payments'}
                                </h1>
                                <p className="text-teal-200 text-sm mt-0.5">Pay generated wages/advance/PF/ESI amounts, CC-wise, by consolidate no.</p>
                            </div>
                        </div>
                        <div className="hidden sm:flex items-center gap-3 text-teal-200">
                            <div className="text-right">
                                <p className="text-xs uppercase tracking-wider">Module</p>
                                <p className="text-sm font-bold text-white">HR / Labour SEP Payment</p>
                            </div>
                            <Navigation className="h-5 w-5 opacity-60" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto space-y-6">

                {/* ── Section 1: Payment Configuration ────────────────── */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <SectionHeader
                        icon={Wallet}
                        title="Payment Configuration"
                        subtitle="Choose payment type, year, month and consolidate no"
                        action={
                            <button
                                onClick={handleFullReset}
                                disabled={isBusy}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"
                            >
                                <RotateCcw className="h-3.5 w-3.5" /> Reset
                            </button>
                        }
                    />
                    <div className="p-6 md:p-8">
                        <InnerHeader icon={Wallet} title="Payment For / Period" subtitle="Changing any of these clears the selection below" />
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">

                            {/* Payment For */}
                            <div>
                                <Label required>Payment For</Label>
                                <div className="relative">
                                    <select
                                        className={`${inputCls} appearance-none pr-10`}
                                        value={paymentFor}
                                        onChange={(e) => setPaymentFor(e.target.value)}
                                        disabled={isBusy || viewed}
                                    >
                                        <option value="">-Please Select-</option>
                                        {PAYMENT_FOR_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                    <SelectIcon loading={false} />
                                </div>
                            </div>

                            {/* Year */}
                            <div>
                                <Label required>{paymentFor ? `${PAYMENT_FOR_OPTIONS.find(o => o.value === paymentFor)?.label} For the Year` : 'Year'}</Label>
                                <div className="relative">
                                    <select
                                        className={`${inputCls} appearance-none pr-10`}
                                        value={year}
                                        onChange={(e) => setYear(e.target.value)}
                                        disabled={!paymentFor || isBusy || viewed}
                                    >
                                        {yearOptions.map((y) => (
                                            <option key={y} value={y}>{y}</option>
                                        ))}
                                    </select>
                                    <SelectIcon loading={false} />
                                </div>
                            </div>

                            {/* Month */}
                            <div>
                                <Label required>{paymentFor ? `${PAYMENT_FOR_OPTIONS.find(o => o.value === paymentFor)?.label} For the Month` : 'Month'}</Label>
                                <div className="relative">
                                    <select
                                        className={`${inputCls} appearance-none pr-10`}
                                        value={month}
                                        onChange={(e) => setMonth(e.target.value)}
                                        disabled={!year || isBusy || viewed}
                                    >
                                        <option value="">-Please Select-</option>
                                        {MONTHS.map((m, i) => (
                                            <option key={i + 1} value={i + 1}>{m}</option>
                                        ))}
                                    </select>
                                    <SelectIcon loading={false} />
                                </div>
                            </div>

                            {/* Consolidate No */}
                            <div>
                                <Label required>Consolidate No</Label>
                                <ConsolidateNoDropdown
                                    options={consolidateOptions}
                                    selected={selectedConsNos}
                                    onChange={setSelectedConsNos}
                                    loading={consolidateLoading}
                                    disabled={!month || isBusy || viewed}
                                />
                            </div>
                        </div>

                        {!consolidateLoading && month && consolidateOptions.length === 0 && (
                            <div className="mt-4 px-4 py-2.5 rounded-xl text-sm font-medium bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300">
                                No Consolidate Nos Exist
                            </div>
                        )}

                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={handleView}
                                disabled={isBusy || viewed || selectedConsNos.length === 0}
                                className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow hover:from-teal-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            >
                                {paidDataLoading
                                    ? <><Loader2 className="h-4 w-4 animate-spin" /> Loading…</>
                                    : <><ListChecks className="h-4 w-4" /> View</>}
                            </button>
                        </div>
                    </div>
                </div>

                {/* ── Not Exist / No Data messages ─────────────────────── */}
                {showNotExist && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                        <div className="px-4 py-3 rounded-xl text-sm font-medium bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 text-center">
                            Payment Already Exist
                        </div>
                    </div>
                )}
                {showNoData && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                        <div className="px-4 py-3 rounded-xl text-sm font-medium bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 text-center">
                            Payroll Paid Amount Does Not Exist For This Month or Payment Type
                        </div>
                    </div>
                )}

                {/* ── Section 2: CC Paid Grid ───────────────────────────── */}
                {showGrid && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <SectionHeader
                            icon={Table2}
                            title="Cost Center Paid Amounts"
                            subtitle="Amounts generated from wages/payroll for the selected consolidate no(s)"
                        />
                        <div className="p-6 md:p-8">
                            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-gradient-to-r from-teal-50 to-emerald-50 dark:from-teal-900/30 dark:to-emerald-900/30">
                                            <th className="px-4 py-3 text-left text-[10px] font-bold text-teal-600 dark:text-teal-300 uppercase tracking-wider">Cost Center</th>
                                            {isLabourPF ? (
                                                <>
                                                    <th className="px-4 py-3 text-right text-[10px] font-bold text-teal-600 dark:text-teal-300 uppercase tracking-wider">Employee Contribution</th>
                                                    <th className="px-4 py-3 text-right text-[10px] font-bold text-teal-600 dark:text-teal-300 uppercase tracking-wider">Employer Contribution</th>
                                                </>
                                            ) : (
                                                <th className="px-4 py-3 text-right text-[10px] font-bold text-teal-600 dark:text-teal-300 uppercase tracking-wider">Amount (₹)</th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paidRows.map((r, idx) => (
                                            <tr key={`${r.CCCode}-${idx}`} className={`border-t border-gray-100 dark:border-gray-700 ${idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/50 dark:bg-gray-700/30'}`}>
                                                <td className="px-4 py-3 text-gray-800 dark:text-gray-100 font-medium">{r.CCName}</td>
                                                {isLabourPF ? (
                                                    <>
                                                        <td className="px-4 py-3 text-right font-semibold text-gray-800 dark:text-gray-100">{formatIndianCurrency(r.CCAmount)}</td>
                                                        <td className="px-4 py-3 text-right font-semibold text-gray-800 dark:text-gray-100">{formatIndianCurrency(r.EmployerAmount)}</td>
                                                    </>
                                                ) : (
                                                    <td className="px-4 py-3 text-right font-semibold text-gray-800 dark:text-gray-100">{formatIndianCurrency(r.CCAmount)}</td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="border-t-2 border-teal-200 dark:border-teal-700 bg-gradient-to-r from-teal-50 to-emerald-50 dark:from-teal-900/30 dark:to-emerald-900/30">
                                            <td className="px-4 py-3 text-xs font-bold text-teal-700 dark:text-teal-300 uppercase tracking-wider">
                                                Total ({paidRows.length} {paidRows.length === 1 ? 'CC' : 'CCs'})
                                            </td>
                                            <td colSpan={isLabourPF ? 2 : 1} className="px-4 py-3 text-right text-base font-black text-teal-700 dark:text-teal-300">
                                                ₹ {formatIndianCurrency(gridTotal)}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                            {gridTotal > 0 && (
                                <p className="text-xs text-teal-500 dark:text-teal-400 mt-1.5 italic text-right">
                                    {getAmountDisplay(gridTotal).words}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* ── Section 3: Bank & Payment Details ────────────────── */}
                {showGrid && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <SectionHeader
                            icon={CreditCard}
                            title="Payment Details"
                            subtitle="Select bank, mode, date and remarks"
                        />
                        <div className="p-6 md:p-8">
                            <InnerHeader icon={Building2} title="Bank Account" subtitle="Select the bank account for this payment" />

                            <div className="mb-6">
                                <Label required>Bank</Label>
                                <div className="relative">
                                    <select
                                        className={`${inputCls} appearance-none pr-10`}
                                        value={selectedBank?.Bank_Id || ''}
                                        onChange={(e) => {
                                            const b = bankList.find((b) => b.Bank_Id === e.target.value);
                                            setSelectedBank(b || null);
                                        }}
                                        disabled={bankLoading || isBusy}
                                    >
                                        <option value="">{bankLoading ? 'Loading…' : '-Please Select-'}</option>
                                        {bankList.map((b) => (
                                            <option key={b.Bank_Id} value={b.Bank_Id}>{b.Bank_Name}</option>
                                        ))}
                                    </select>
                                    <SelectIcon loading={bankLoading} />
                                </div>
                            </div>

                            <InnerHeader icon={CreditCard} title="Mode & Reference" />

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <Label required>Mode of Pay</Label>
                                    <div className="relative">
                                        <select
                                            className={`${inputCls} appearance-none pr-10`}
                                            value={modeOfPay}
                                            onChange={(e) => setModeOfPay(e.target.value)}
                                            disabled={!selectedBank || isBusy}
                                        >
                                            <option value="">{!selectedBank ? 'Select bank first' : '-Please Select-'}</option>
                                            {MODE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                                        </select>
                                        <SelectIcon loading={false} />
                                    </div>
                                </div>

                                {modeOfPay === 'Cheque' ? (
                                    <div>
                                        <Label required>Cheque No</Label>
                                        <div className="relative">
                                            <select
                                                className={`${inputCls} appearance-none pr-10`}
                                                value={chequeId}
                                                onChange={(e) => setChequeId(e.target.value)}
                                                disabled={chequeLoading || isBusy}
                                            >
                                                <option value="">{chequeLoading ? 'Loading…' : '-Select-'}</option>
                                                {chequeList.map((c) => (
                                                    <option key={c.Cheque_Id} value={c.Cheque_Id}>{c.Cheque_No}</option>
                                                ))}
                                            </select>
                                            <SelectIcon loading={chequeLoading} />
                                        </div>
                                    </div>
                                ) : modeOfPay ? (
                                    <div>
                                        <Label required>Payment No</Label>
                                        <div className="relative">
                                            <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                            <input
                                                type="text"
                                                className={`${inputCls} pl-9`}
                                                placeholder="Enter payment number"
                                                value={refNo}
                                                onChange={(e) => setRefNo(e.target.value)}
                                                disabled={isBusy}
                                            />
                                        </div>
                                    </div>
                                ) : <div />}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <Label required>Payment Date</Label>
                                    <CustomDatePicker
                                        value={payDate}
                                        onChange={setPayDate}
                                        disabled={isBusy}
                                        maxDate={new Date()}
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <Label required>Remarks</Label>
                                    <textarea
                                        rows={2}
                                        placeholder="Enter remarks…"
                                        value={remarks}
                                        onChange={(e) => setRemarks(e.target.value)}
                                        disabled={isBusy}
                                        className={`${inputCls} resize-none`}
                                    />
                                </div>
                            </div>

                            <div className="mt-6">
                                <Label>Payment Amount (₹)</Label>
                                <input
                                    type="text"
                                    readOnly
                                    value={formatIndianCurrency(gridTotal)}
                                    className={`${inputCls} max-w-xs font-bold`}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Summary ───────────────────────────────────────────── */}
                {showSummary && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <SectionHeader icon={CheckCircle} title="Payment Summary" subtitle="Review before submitting" />
                        <div className="p-6 md:p-8">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                                {[
                                    { label: 'Payment For', value: PAYMENT_FOR_OPTIONS.find(o => o.value === paymentFor)?.label },
                                    { label: 'Period', value: `${MONTHS[parseInt(month) - 1]} ${year}` },
                                    { label: 'No. of CCs', value: paidRows.length },
                                    { label: 'Total Amount', value: `₹ ${formatIndianCurrency(gridTotal)}` },
                                    { label: 'Bank', value: selectedBank?.Bank_Name },
                                    { label: 'Mode', value: modeOfPay },
                                    { label: modeOfPay === 'Cheque' ? 'Cheque No' : 'Payment No', value: refDisplay },
                                    { label: 'Payment Date', value: dateDisplay },
                                    { label: 'Created By', value: userName },
                                    { label: 'In Words', value: getAmountDisplay(gridTotal).words, wide: true },
                                    { label: 'Remarks', value: remarks || '—', wide: true },
                                ].map(({ label, value, wide }) => (
                                    <div key={label} className={`bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 ${wide ? 'md:col-span-3' : ''}`}>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
                                        <p className="font-semibold text-gray-800 dark:text-gray-100 break-words">{value ?? '—'}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Action Buttons ────────────────────────────────────── */}
                {showGrid && (
                    <div className="flex items-center justify-end gap-3 pb-6">
                        <button
                            onClick={handleFullReset}
                            disabled={isBusy}
                            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"
                        >
                            <RotateCcw className="h-4 w-4" /> Reset
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={!canSubmit}
                            className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-md shadow-teal-500/30 hover:from-teal-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            {saveLoading
                                ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>
                                : <><Send className="h-4 w-4" /> Submit</>}
                        </button>
                    </div>
                )}

            </div>
        </div>
    );
};

export default LabourSepPayment;
